import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import '../../lib/intersectionObserverPolyfill'; 

import { logsService } from '../../Services';
import { itemsPerPage } from '../../Services'

import {ReactComponent as Loading} from '../../images/icons/General/loading-01.svg';
import {ReactComponent as Download} from '../../images/icons/General/download-cloud-02.svg';
import {ReactComponent as Printer} from '../../images/icons/Media/printer.svg';
import {ReactComponent as Filter} from '../../images/icons/General/filter-funnel-01.svg';

const depthRanges = [
    { value: '30', label: '0 - 30 ft' },
    { value: '60', label: '31 - 60 ft' },
    { value: '100', label: '61 - 100 ft' },
    { value: '130', label: '101 - 130 ft' },
    { value: '150', label: '131 - 150 ft' },
    { value: '190', label: '151 - 190 ft' },
    { value: '191', label: '191+ ft' },
];

const environments = [
    { value: 'RequiredDecompression', label: 'Required Decompression' },
    { value: 'OverheadEnvironment', label: 'Overhead Environment' },
    { value: 'BlueWater', label: 'Blue Water' },
    { value: 'IcePolar', label: 'Ice/Polar' },
    { value: 'SaturationDiving', label: 'Saturation Diving' },
    { value: 'AquariumDiving', label: 'Aquarium Diving' },
];

const emptyFilters = {
    dateFrom: null,
    dateTo: null,
    location: null,
    gear: null,
    depth: null,
    classification: null,
    decompression: null,
    environment: null,
};

const selectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '44px',
        borderRadius: '8px',
        border: '1px solid #D0D5DD',
        '&:hover': {
            boxShadow: '0 0 1px #5D3FC5'
        }
    })
};

const isSameDate = (a, b) => (a ? a.getTime() : null) === (b ? b.getTime() : null);

// applies the date only when it is fully entered: picked in the calendar, or when the field loses focus
const DateFilter = ({ value, onChange, ...props }) => {
    const [draft, setDraft] = useState(value);
    const draftRef = useRef(value);
    const committedRef = useRef(value);

    useEffect(() => {
        draftRef.current = value;
        committedRef.current = value;
        setDraft(value);
    }, [value]);

    const commit = (date) => {
        if (isSameDate(date, committedRef.current)) return;
        committedRef.current = date;
        onChange(date);
    }

    const handleChange = (date, e) => {
        draftRef.current = date;
        setDraft(date);

        // clear button
        if (date === null && e?.type === 'click')
            commit(null);
    }

    return (
        <DatePicker
            {...props}
            selected={draft}
            onChange={handleChange}
            onSelect={commit}
            onCalendarClose={() => commit(draftRef.current)}
            onBlur={() => commit(draftRef.current)}
            onKeyDown={(e) => e.key === 'Enter' && commit(draftRef.current)}
        />
    );
}

export const History = () => {
    const params = useParams();
    const { userId } = params;
    const [searchParams, setSearchParams] = useSearchParams();
    const [info, setInfo] = useState([]);
    const [user, setUser] = useState({});
    const [number, setNumber] = useState(0);
    const [numberYear, setNumberYear] = useState(0);
    const [loadingCsv, setLoadingCsv] = useState(false);
    const [loadingFeedingsCsv, setLoadingFeedingsCsv] = useState(false);
    const [sortField, setSortField] = useState('DiveStartTime');
    const [sortOrder, setSortOrder] = useState(false);
    const [page, setPage] = useState(-1);
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState(emptyFilters);
    const [showFilters, setShowFilters] = useState(false);
    const [loading, setLoading] = useState(false);
    const pendingRequestsRef = useRef(0);
    const requestGenerationRef = useRef(0);
    const [filterOptions, setFilterOptions] = useState({});
    const [filteredNumber, setFilteredNumber] = useState(null);
    const loaderRef = useRef(null);
    const tableRef = useRef(null);
    const initialPageRef = useRef(Math.max(parseInt(searchParams.get('page')) || 1, 1) - 1);
    const firstLoadRef = useRef(true);
    const scrollToIndexRef = useRef(null);
    const modules = useSelector((state) => state.org.modules);
    const local = useSelector((state) => state.org.remember);
    const name = useSelector((state) => state[local ? 'localUser' : 'user'].name).replace(' ', '_');

    const resetValues = () => {
        // responses of requests started before the reset are ignored
        requestGenerationRef.current++;
        setInfo([]);
        setPage(-1);
        setHasMore(true);
        setNumber(0);
        setNumberYear(0);
        setFilteredNumber(null);
    }

    const changeFilter = (key, value) => {
        if (loading) return;
        setFilters(prev => ({...prev, [key]: value}));
        resetValues();
    }

    const clearFilters = () => {
        if (loading) return;
        setFilters(emptyFilters);
        resetValues();
    }

    const activeFiltersCount = Object.values(filters).filter(value => value !== null).length;
    const hasFilters = activeFiltersCount > 0;

    const sort = (e) => {
        if (loading) return;
        if (e.target.id === sortField)
            setSortOrder(!sortOrder)
        else 
            setSortField(e.target.id);

        resetValues();
    }

    const printTable = async () => {
        const res = await logsService.historyPrint(params.userId || 0);
        const rows = res.list.map(item => `
            <tr>
                <td>${item.date}</td>
                <td>${item.time}</td>
                <td>${item.totalTime}</td>
                <td>${item.location}</td>
                <td>${item.gear}</td>
                <td>${item.diveDepth}</td>
                <td>${item.comments || ''}</td>
            </tr>`).join('');
        const table = `<table>
            <thead><tr>
                <th>Date</th><th>Starting time</th><th>Duration (min)</th>
                <th>Location</th><th>Gear</th><th>Depth (ft)</th><th>Comments</th>
            </tr></thead>
            <tbody>${rows}</tbody>
        </table>`;
        // eslint-disable-next-line
        const style = '<style>table {border: 1px solid #000; font-family: Tahoma; border-spacing: 0;}\
            td,th {border-bottom: 1px solid #000; border-right: 1px solid #000; padding: 5px 10px;}\
            td:last-child,th:last-child{border-right: 0;}</style>';
        window.frames["print_frame"].document.body.innerHTML = table + style;
        window.frames["print_frame"].window.focus();
        window.frames["print_frame"].window.print();
    }
    
    const downloadCsv = async () => {
        setLoadingCsv(true);
        const date = new Date();
        const res = await logsService.historyExport(params.userId || 0);
        const a = document.createElement('a');
        let binaryData = [];
        binaryData.push(res);
        let url = window.URL.createObjectURL(new Blob(binaryData, {type: "text/csv"}))
        a.href = url;
        a.download = (params.userId ? `${user.LastName} ${user.FirstName}` : name) + '_dive-log_'+window.location.hostname+'_'+moment(date).format('YYYY-MM-DD-HH-mm')+'.csv';
        document.body.append(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setLoadingCsv(false);
    };

    const downloadFeedingsCsv =  async () => {
        setLoadingFeedingsCsv(true);
        const date = new Date();
        const res = await logsService.feedingsExport(params.userId || 0);
        const a = document.createElement('a');
        let binaryData = [];
        binaryData.push(res);
        let url = window.URL.createObjectURL(new Blob(binaryData, {type: "text/csv"}))
        a.href = url;
        a.download = (params.userId ? `${user.LastName} ${user.FirstName}` : name) + '_feeding-log_'+window.location.hostname+'_'+moment(date).format('YYYY-MM-DD-HH-mm')+'.csv';
        document.body.append(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setLoadingFeedingsCsv(false);
    }

    const fetchData = useCallback(async (pageNum, pagesCount, sortFieldValue, sortOrderValue, filtersValue) => {
        const filterParams = {};
        Object.keys(filtersValue).forEach(key => {
            const value = filtersValue[key];
            if (value === null) return;
            filterParams[key] = value instanceof Date ? moment(value).format('YYYY-MM-DD') : value.value;
        });

        const generation = requestGenerationRef.current;
        pendingRequestsRef.current++;
        setLoading(true);

        let res;
        try {
            res = await logsService.history(userId || 0, pageNum, sortFieldValue, sortOrderValue, pagesCount, filterParams);
        } finally {
            pendingRequestsRef.current--;
            setLoading(pendingRequestsRef.current > 0);
        }

        if (generation !== requestGenerationRef.current || !res?.list)
            return;

        setInfo(prev => [...prev, ...res.list]);

        if (pagesCount > 1)
            scrollToIndexRef.current = itemsPerPage * (pagesCount - 1);

        if (res.divesNumber)
            setNumber(res.divesNumber);

        if (res.divesNumberYear)
            setNumberYear(res.divesNumberYear);
        
        if (res.user)
            setUser(res.user);

        if (res.filterOptions)
            setFilterOptions(res.filterOptions);

        if (res.filteredNumber !== null && res.filteredNumber !== undefined)
            setFilteredNumber(res.filteredNumber);

        if (res.list.length < itemsPerPage * pagesCount)
            setHasMore(false);
      }, [userId]);

    useEffect(() => {
        const observer = new IntersectionObserver(
        (entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setPage((prev) => prev === -1 && firstLoadRef.current ? initialPageRef.current : prev + 1);
            }
        },
        { threshold: 0.1 }
        );

        const loader = loaderRef.current;
        if (loader) observer.observe(loader);

        return () => {
            if (loader) observer.unobserve(loader);
        };
    }, [hasMore]);

    useEffect(() => {
        if (page < 0) return;
        // on the first load fetch all pages up to the one from the url in one request
        const pagesCount = firstLoadRef.current ? page + 1 : 1;
        firstLoadRef.current = false;
        fetchData(page + 1 - pagesCount, pagesCount, sortField, sortOrder, filters);
    }, [fetchData, page, sortField, sortOrder, filters]);

    useEffect(() => {
        const index = scrollToIndexRef.current;
        if (index === null) return;
        scrollToIndexRef.current = null;
        const row = tableRef.current?.tBodies[0]?.rows[index];
        if (row)
            row.scrollIntoView({ block: 'start' });
    }, [info]);

    useEffect(() => {
        if (page < 0) return;
        const urlPage = page > 0 ? String(page + 1) : null;
        if (searchParams.get('page') === urlPage) return;
        const newParams = new URLSearchParams(searchParams);
        if (urlPage)
            newParams.set('page', urlPage);
        else
            newParams.delete('page');
        setSearchParams(newParams, { replace: true });
    }, [page, searchParams, setSearchParams]);
    
    return (
        <>
            <h1>{params.userId ? (user?.FirstName ? `${user.FirstName} ${user.LastName}'s` : `User's`) : 'My'} dive log history</h1>
            <div className="bordered-content">
                <div className="header">
                    <h4>Dives <span className="label">{number} logged dive{number === 1 ? '' : 's'}, {numberYear} of which in the last 12 months</span>{filteredNumber !== null ? <span className="label">{filteredNumber} matching filters</span> : ''}</h4>
                    <p>If you would like to see all columns, please download the CSV</p>
                    <div className="buttons">
                        <button type="button" className={`default filters-toggle ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)} aria-expanded={showFilters} aria-controls="history-filters"><Filter />Filters{hasFilters ? <span className="filters-count">{activeFiltersCount}</span> : ''}</button>
                        <button className="default" onClick={printTable}><Printer />Print</button>
                        <button className="default" onClick={downloadCsv}>{loadingCsv ? <Loading className="btn-loader" alt="loader" /> : <Download />}Download CSV</button>
                        {modules.includes('Feedings') && <button className="default" onClick={downloadFeedingsCsv}>{loadingFeedingsCsv ? <Loading className="btn-loader" alt="loader" /> : <Download />}Download Feedings</button>}
                        <iframe name="print_frame" title="print_frame" width="0" height="0" frameBorder="0" src="about:blank"></iframe>
                    </div>
                </div>
                {showFilters && <div className={`history-filters ${loading ? 'loading' : ''}`} id="history-filters" aria-busy={loading}>
                    <div className="history-filter">
                        <label>Dive date from</label>
                        <DateFilter value={filters.dateFrom} onChange={(value) => changeFilter('dateFrom', value)} maxDate={filters.dateTo} placeholderText="Any date" disabled={loading} isClearable />
                    </div>
                    <div className="history-filter">
                        <label>Dive date to</label>
                        <DateFilter value={filters.dateTo} onChange={(value) => changeFilter('dateTo', value)} minDate={filters.dateFrom} placeholderText="Any date" disabled={loading} isClearable />
                    </div>
                    <div className="history-filter">
                        <label>Location</label>
                        <Select options={filterOptions.locations || []} value={filters.location} onChange={(value) => changeFilter('location', value)} styles={selectStyles} placeholder="All locations" isDisabled={loading} isClearable isSearchable />
                    </div>
                    <div className="history-filter">
                        <label>Gear</label>
                        <Select options={filterOptions.gears || []} value={filters.gear} onChange={(value) => changeFilter('gear', value)} styles={selectStyles} placeholder="All gear" isDisabled={loading} isClearable />
                    </div>
                    <div className="history-filter">
                        <label>Depth range</label>
                        <Select options={depthRanges} value={filters.depth} onChange={(value) => changeFilter('depth', value)} styles={selectStyles} placeholder="All depths" isDisabled={loading} isClearable />
                    </div>
                    <div className="history-filter">
                        <label>Classification</label>
                        <Select options={filterOptions.classifications || []} value={filters.classification} onChange={(value) => changeFilter('classification', value)} styles={selectStyles} placeholder="All classifications" isDisabled={loading} isClearable />
                    </div>
                    <div className="history-filter">
                        <label>Decompression</label>
                        <Select options={filterOptions.decompression || []} value={filters.decompression} onChange={(value) => changeFilter('decompression', value)} styles={selectStyles} placeholder="All methods" isDisabled={loading} isClearable />
                    </div>
                    <div className="history-filter">
                        <label>Specialized environment</label>
                        <Select options={environments} value={filters.environment} onChange={(value) => changeFilter('environment', value)} styles={selectStyles} placeholder="All environments" isDisabled={loading} isClearable />
                    </div>
                    {hasFilters ? <div className="history-filter clear">
                        <button type="button" className="default" onClick={clearFilters} disabled={loading}>Clear filters</button>
                    </div> : ''}
                </div>}
                <table id="history-table" ref={tableRef} className={loading ? 'loading' : ''}>
                    <thead>
                        <tr>
                            <th className={`sortable ${sortField === 'DiveStartTime' ? (sortOrder ? 'down' : 'up') : ''}`} id="DiveStartTime" onClick={sort}>Date</th>
                            <th className={`sortable ${sortField === 'DiveStartTime' ? (sortOrder ? 'down' : 'up') : ''}`} id="DiveStartTime" onClick={sort}>Starting time</th>
                            <th className={`sortable ${sortField === 'totalTime' ? (sortOrder ? 'down' : 'up') : ''}`} id="totalTime" onClick={sort}>Duration (min)</th>
                            <th className={`sortable ${sortField === 'location' ? (sortOrder ? 'down' : 'up') : ''}`} id="location" onClick={sort}>Location</th>
                            <th className={`sortable ${sortField === 'gear' ? (sortOrder ? 'down' : 'up') : ''}`} id="gear" onClick={sort}>Gear</th>
                            <th className={`sortable ${sortField === 'diveDepth' ? (sortOrder ? 'down' : 'up') : ''}`} id="diveDepth" onClick={sort}>Depth (ft)</th>
                            <th className="light">Comments</th>
                        </tr>
                    </thead>
                    <tbody>
                        {info.length ? info.map(item => {
                            return (
                                <tr key={item.id}>
                                    <td><Link to={params.userId ? `/configuration/users/${params.userId}/dives/${item.id}` : `/logs/edit/${item.id}`}>{item.date}<span className="mobile"> {item.time}</span></Link></td>
                                    <td className="desktop">{item.time}</td>
                                    <td><strong className="mobile">Duration: </strong>{item.totalTime}</td>
                                    <td><strong className="mobile">Location: </strong>{item.location}</td>
                                    <td><strong className="mobile">Gear: </strong><span className="label desktop">{item.gear}</span> <span className="mobile">{item.gear}</span></td>
                                    <td><strong className="mobile">Depth: </strong>{item.diveDepth} <span className="mobile">ft</span></td>
                                    <td className="light">{item.comments}</td>
                                </tr>
                            )}
                        ) : (hasMore ? <></> : <tr><td colSpan="9" className="load-more">No dives found</td></tr>)}
                        {hasMore && <tr ref={loaderRef}><td colSpan="9" className="load-more">Loading more...</td></tr>}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default History;